import React, { useEffect, useState } from 'react';
import './ChampionItemBuild.css';

// Interfaces matching the backend data model
export interface CoreItemBuild {
  item1_id: number;
  item1_name: string;
  item2_id: number;
  item2_name: string;
  item3_id: number;
  item3_name: string;
  times_seen: number;
  pct_of_players: number;
}

export interface SupplementaryItem {
  item_id: number;
  item_name: string;
  most_common_slot: number;
  slot_buys: number;
  pct_in_that_slot: number;
}

export interface BootItem {
  item_id: number;
  name: string;
  pick_count: number;
}

export interface StarterItem {
  item_id: number;
  name: string;
  pick_count: number;
}

export interface FinalSupportItem {
  item_id: number;
  name: string;
  pick_count: number;
}

export interface ChampionItemBuildData {
  coreItems: CoreItemBuild;
  supplementaryItems: SupplementaryItem[];
  boots: BootItem | null;
  starter: StarterItem | null;
  finalSupportItem?: FinalSupportItem | null;
}

// Support item IDs
const FINAL_SUPPORT_ITEM_IDS = new Set([3869, 3870, 3871, 3876, 3877]);
const ALL_SUPPORT_ITEM_IDS = new Set([
  3865, 3866, 3867, // World Atlas, Runic Compass, Bounty of Worlds
  3869, 3870, 3871, 3876, 3877 // Final support items
]);

// Jungle starter item IDs
const JUNGLE_STARTER_IDS = new Set([1101, 1102, 1103]); // Scorchclaw Pup, Gustwalker Hatchling, Mosstomper Seedling
const DEFAULT_JUNGLE_STARTER = { item_id: 1101, name: 'Scorchclaw Pup' };
const DEFAULT_NON_JUNGLE_STARTER = { item_id: 1055, name: "Doran's Blade" };

interface Props {
  championName: string;
  championLane?: string; // e.g. 'UTILITY', 'BOTTOM', etc.
}

const ChampionItemBuild: React.FC<Props> = ({ championName, championLane }) => {
  const [loading, setLoading] = useState(true);
  const [itemBuildData, setItemBuildData] = useState<ChampionItemBuildData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [apiVersion, setApiVersion] = useState<string>("15.9.1"); // Default version as fallback
  
  // Fetch the latest API version when component mounts
  useEffect(() => {
    const fetchApiVersion = async () => {
      try {
        const response = await fetch('https://ddragon.leagueoflegends.com/api/versions.json');
        if (response.ok) {
          const versions = await response.json();
          if (versions && versions.length > 0) {
            setApiVersion(versions[0]); // Use the latest version
            console.log(`Using Data Dragon version: ${versions[0]}`);
          }
        }
      } catch (err) {
        console.warn('Error fetching API version, using default:', err);
        // Continue with default version
      }
    };
    
    fetchApiVersion();
  }, []);
  
  useEffect(() => {
    const fetchItemBuild = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/item-build`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch item build data: ${response.status}`);
        }
        
        const data = await response.json();
        setItemBuildData(data);
        console.log('Fetched item build data:', data);
      } catch (err) {
        console.error('Error fetching item build data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    if (championName) {
      fetchItemBuild();
    }
  }, [championName]);
  
  // Helper function to get item image URL using current API version
  const getItemImageUrl = (itemId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };
  
  if (loading) {
    return <div className="item-build-loading">Loading item build data...</div>;
  }
  
  if (error) {
    return <div className="item-build-error">Error: {error}</div>;
  }
  
  if (!itemBuildData) {
    return <div className="item-build-missing">No item build data available for {championName}.</div>;
  }
  
  const isSupport = championLane === 'UTILITY';
  const isJungle = championLane === 'JUNGLE';
  const { coreItems, supplementaryItems, starter, finalSupportItem } = itemBuildData;
  
  // For support: build a corrected core items display
  // - Starter: World Atlas (3865)
  // - Core item 1: Final support item (from API)
  // - Core items 2-3: Non-support legendary items from core/supplementary
  // - Supplementary: remaining items, excluding support items
  let displayStarter: { item_id: number; name: string } | null = starter;
  let displayCoreItems: { id: number; name: string }[] = [];
  let displaySupplementary: SupplementaryItem[] = [];

  // Jungle starter override:
  // - If role IS jungle but starter is NOT a jungle item → force Scorchclaw Pup
  // - If role is NOT jungle and NOT support but starter IS a jungle item → force Doran's Blade
  if (isJungle) {
    if (!displayStarter || !JUNGLE_STARTER_IDS.has(displayStarter.item_id)) {
      displayStarter = DEFAULT_JUNGLE_STARTER;
    }
  } else if (!isSupport) {
    if (displayStarter && JUNGLE_STARTER_IDS.has(displayStarter.item_id)) {
      displayStarter = DEFAULT_NON_JUNGLE_STARTER;
    }
  }

  if (isSupport) {
    displayStarter = { item_id: 3865, name: 'World Atlas' };
    
    // Collect all non-support core items
    const rawCoreIds = [
      { id: coreItems.item1_id, name: coreItems.item1_name },
      { id: coreItems.item2_id, name: coreItems.item2_name },
      { id: coreItems.item3_id, name: coreItems.item3_name },
    ];
    
    const nonSupportCore = rawCoreIds.filter(item => !ALL_SUPPORT_ITEM_IDS.has(item.id));
    const nonSupportSupp = supplementaryItems.filter(item => !ALL_SUPPORT_ITEM_IDS.has(item.item_id));
    
    // Final support item goes first
    if (finalSupportItem) {
      displayCoreItems.push({ id: finalSupportItem.item_id, name: finalSupportItem.name });
    }
    
    // Fill remaining core slots from non-support core items, then supplementary
    for (const item of nonSupportCore) {
      if (displayCoreItems.length >= 3) break;
      displayCoreItems.push(item);
    }
    for (const item of nonSupportSupp) {
      if (displayCoreItems.length >= 3) break;
      displayCoreItems.push({ id: item.item_id, name: item.item_name });
    }
    
    // Supplementary: remaining non-support items not already used in core
    const usedIds = new Set(displayCoreItems.map(i => i.id));
    displaySupplementary = nonSupportSupp.filter(item => !usedIds.has(item.item_id));
  } else {
    displayCoreItems = [
      { id: coreItems.item1_id, name: coreItems.item1_name },
      { id: coreItems.item2_id, name: coreItems.item2_name },
      { id: coreItems.item3_id, name: coreItems.item3_name },
    ];
    displaySupplementary = supplementaryItems;
  }
  
  // Prepare option items
  // First option column - 2 items
  const optionColumn1 = displaySupplementary.slice(0, Math.min(2, displaySupplementary.length));
  // Second option column - 3 items
  const optionColumn2 = displaySupplementary.slice(Math.min(2, displaySupplementary.length), Math.min(5, displaySupplementary.length));
  
  return (
    <div className="champion-item-build-container">
      <div className="item-build-layout">
        {/* Starter Item */}
        {displayStarter && (
          <div className="cib-item-slot">
            <img src={getItemImageUrl(displayStarter.item_id)} alt={displayStarter.name} className="cib-item-image" title={displayStarter.name} />
          </div>
        )}
        
        {/* Simplified Column Divider */}
        <div className="column-divider">
          <div className="divider-line"></div>
        </div>
        
        {/* Core Items and Options */}
        <div className="build-path-content">
          <div className="core-items-row">
            {/* Core Items */}
            {displayCoreItems.map((item, idx) => (
              <React.Fragment key={`core-${idx}`}>
                {idx > 0 && <div className="arrow">→</div>}
                <div className="cib-item-slot">
                  <img src={getItemImageUrl(item.id)} alt={item.name} className="cib-item-image" title={item.name} />
                </div>
              </React.Fragment>
            ))}
            
            {/* Arrow pointing to options */}
            {displaySupplementary.length > 0 && (
              <div className="arrow options-arrow">→</div>
            )}
            
            {/* Options layout */}
            {displaySupplementary.length > 0 && (
              <div className="options-layout">
                {/* First options column (2 items) */}
                <div className="options-column column1">
                  {optionColumn1.map((item, idx) => (
                    <div key={`option-col1-${idx}`} className="cib-item-slot">
                      <img 
                        src={getItemImageUrl(item.item_id)} 
                        alt={item.item_name} 
                        className="cib-item-image" 
                        title={`${item.item_name} (Slot ${item.most_common_slot}, ${item.pct_in_that_slot}%)`} 
                      />
                    </div>
                  ))}
                </div>
                
                {/* Arrow to second options column */}
                {optionColumn2.length > 0 && (
                  <>
                    <div className="arrow column-arrow">→</div>
                    {/* Second options column (3 items) */}
                    <div className="options-column column2">
                      {optionColumn2.map((item, idx) => (
                        <div key={`option-col2-${idx}`} className="cib-item-slot">
                          <img 
                            src={getItemImageUrl(item.item_id)} 
                            alt={item.item_name} 
                            className="cib-item-image" 
                            title={`${item.item_name} (Slot ${item.most_common_slot}, ${item.pct_in_that_slot}%)`} 
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChampionItemBuild; 